import React from 'react';
import { TrendingUp, BarChart3, PieChart, Activity } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart as RechartsPie, Pie, Cell } from 'recharts';

export default function ImpactAnalysisPanel({ refinements, stats }) {
  // Prepare data for charts
  const statusData = [
    { name: 'En Attente', value: stats?.pending || 0, color: '#f59e0b' },
    { name: 'Approuvés', value: stats?.approved || 0, color: '#16a34a' },
    { name: 'Implémentés', value: stats?.implemented || 0, color: '#3b82f6' },
    { name: 'Rejetés', value: stats?.rejected || 0, color: '#ef4444' }
  ];

  const typeData = Object.entries(stats?.byType || {})?.map(([type, count]) => ({
    name: type?.replace('_', ' '),
    count
  }));

  const COLORS = ['#16a34a', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6'];

  // Calculate metrics
  const approvalRate = stats?.total > 0
    ? ((stats?.approved + stats?.implemented) / stats?.total * 100)?.toFixed(1)
    : 0;

  const implementationRate = stats?.approved > 0
    ? (stats?.implemented / stats?.approved * 100)?.toFixed(1)
    : 0;

  return (
    <div className="space-y-6">
      {/* Summary Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <TrendingUp size={24} />
          </div>
          <p className="text-2xl font-bold">{approvalRate}%</p>
          <p className="text-sm opacity-90">Taux d'Approbation</p>
        </div>

        <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <Activity size={24} />
          </div>
          <p className="text-2xl font-bold">{implementationRate}%</p>
          <p className="text-sm opacity-90">Taux d'Implémentation</p>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <BarChart3 size={24} />
          </div>
          <p className="text-2xl font-bold">{stats?.total || 0}</p>
          <p className="text-sm opacity-90">Suggestions Totales</p>
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg p-6 text-white">
          <div className="flex items-center justify-between mb-2">
            <PieChart size={24} />
          </div>
          <p className="text-2xl font-bold">{stats?.highPriority || 0}</p>
          <p className="text-sm opacity-90">Priorité Haute</p>
        </div>
      </div>
      {/* Status Distribution */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Distribution par Statut</h3>
        <ResponsiveContainer width="100%" height={300}>
          <RechartsPie>
            <Pie
              data={statusData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100)?.toFixed(0)}%)`}
              outerRadius={100}
              fill="#8884d8"
              dataKey="value"
            >
              {statusData?.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry?.color} />
              ))}
            </Pie>
            <Tooltip />
          </RechartsPie>
        </ResponsiveContainer>
      </div>
      {/* Type Distribution */}
      {typeData?.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Distribution par Type</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={typeData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="count" fill="#16a34a" name="Nombre de Suggestions" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
      {/* Recent Activity */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Impact des Améliorations</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
            <div>
              <p className="font-medium text-gray-900">FAQs Améliorées</p>
              <p className="text-sm text-gray-600">Suggestions de type FAQ implémentées</p>
            </div>
            <p className="text-2xl font-bold text-green-600">
              {refinements?.filter(r => r?.status === 'implemented' && r?.refinement_type?.includes('faq'))?.length || 0}
            </p>
          </div>

          <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
            <div>
              <p className="font-medium text-gray-900">Connaissances Mises à Jour</p>
              <p className="text-sm text-gray-600">Mises à jour de la base de connaissances</p>
            </div>
            <p className="text-2xl font-bold text-blue-600">
              {refinements?.filter(r => r?.status === 'implemented' && r?.refinement_type === 'knowledge_update')?.length || 0}
            </p>
          </div>

          <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg">
            <div>
              <p className="font-medium text-gray-900">Produits Ajoutés/Modifiés</p>
              <p className="text-sm text-gray-600">Mises à jour des informations produits</p>
            </div>
            <p className="text-2xl font-bold text-purple-600">
              {refinements?.filter(r => r?.status === 'implemented' && r?.refinement_type === 'product_update')?.length || 0}
            </p>
          </div>
        </div>
      </div>
      {/* Recommendations */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">💡 Recommandations</h3>
        <ul className="space-y-2 text-sm text-gray-700">
          {stats?.pending > 5 && (
            <li>• Vous avez {stats?.pending} suggestions en attente. Considérez de les revoir pour améliorer Fatou.</li>
          )}
          {stats?.highPriority > 0 && (
            <li>• {stats?.highPriority} suggestions à priorité haute nécessitent une attention immédiate.</li>
          )}
          {stats?.approved > 0 && (
            <li>• {stats?.approved} suggestions approuvées sont prêtes à être appliquées.</li>
          )}
          {parseFloat(approvalRate) < 50 && stats?.total > 10 && (
            <li>• Le taux d'approbation est faible ({approvalRate}%). Considérez d'améliorer le processus de suggestion.</li>
          )}
        </ul>
      </div>
    </div>
  );
}